import { useNavigate } from "react-router-dom";
import { NuplayWordmark } from "./components/NuplayWordmark";

// Index of /nurix-experience — the two headline demos side by side. Each card
// routes to that vertical's own landing page, which in turn deep-links into the
// shared call surface.

interface Demo {
  route: string;
  eyebrow: string;
  title: string;
  brand: string;
  /** e.g. "4-agent crew" — the one fact a first meeting always asks about. */
  scale: string;
  desc: string;
  chips: string[];
  tileGradient: string;
  icon: React.ReactNode;
}

const DEMOS: Demo[] = [
  {
    route: "/nurix-experience/victorias-secret",
    eyebrow: "Retail — apparel & beauty",
    title: "Victoria's Secret",
    brand: "Ava · Customer Care",
    scale: "6-agent crew",
    desc: "Orders, recovery, returns, gifting and escalations behind one voice. When the last unit is “showing one left,” Ava warm-transfers the store to physically check it before you travel.",
    chips: ["Partial delivery", "Assisted returns", "Gifting", "Cancel guardrail"],
    tileGradient: "linear-gradient(135deg, #ED1C6F 0%, #111111 100%)",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
    ),
  },
  {
    route: "/nurix-experience/fashion",
    eyebrow: "Retail — apparel care",
    title: "NuPlay Fashion",
    brand: "Mira · Customer Care",
    scale: "4-agent crew",
    desc: "Orders, returns, complaints and styling behind one voice. Ask an Icon member where her refund is, then ask a Member the same thing — the loyalty tier decides the answer.",
    chips: ["Where is my order", "Returns & refunds", "Store pickup", "Styling"],
    tileGradient: "linear-gradient(135deg, #E11D74 0%, #7C1D5F 100%)",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 3l4 2.5-2.5 3.5L16 8v13H8V8l-1.5 1L4 5.5 8 3a4 4 0 0 0 8 0z" />
      </svg>
    ),
  },
  {
    route: "/nurix-experience/nova",
    eyebrow: "BFSI — consumer finance",
    title: "Nova Money",
    brand: "Meera · Customer Care",
    scale: "5-desk crew",
    desc: "Loans, cards, disputes, payments and KYC. It blocks a lost card on the call and will never unblock one — the interesting part is what it refuses.",
    chips: ["Disputed charges", "Foreclosure quotes", "Failed transfers", "KYC"],
    tileGradient: "linear-gradient(135deg, #0EA5E9 0%, #1D4885 100%)",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1.5" x2="12" y2="22.5" /><path d="M17 5.5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    route: "/nurix-experience/fintech",
    eyebrow: "BFSI — B2B payments",
    title: "Fintech",
    brand: "Claire · Client Services",
    scale: "Single agent",
    desc: "Tier-driven answers on invoices, drafts and compliance holds — the same question gets a different promise on Platinum than it does on Silver.",
    chips: ["Returned drafts", "Delivery holds", "Accounting sync"],
    tileGradient: "linear-gradient(135deg, #2582EB 0%, #1D4885 100%)",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /><line x1="6" y1="15" x2="10" y2="15" />
      </svg>
    ),
  },
  {
    route: "/nurix-experience/retail",
    eyebrow: "Retail — order support",
    title: "NuPlay Electronics",
    brand: "Anna · Customer Support",
    scale: "Live storefront",
    desc: "WISMO, cancellations, store pickup and recommendations — a full order-support conversation against a live Shopify storefront.",
    chips: ["Where is my order", "Cancellations", "Store pickup"],
    tileGradient: "linear-gradient(135deg, #34d399 0%, #059669 100%)",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
  },
];

function DemoCard({ demo }: { demo: Demo }) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      onClick={() => navigate(demo.route)}
      className="group w-full h-full flex flex-col text-left rounded-2xl overflow-hidden transition-all duration-150 active:scale-[0.99]"
      style={{ background: "var(--nx-card-bg)", border: "1px solid var(--nx-card-border)", backdropFilter: "blur(20px)" }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(37,130,235,0.30)";
        (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 20px 56px rgba(0,0,0,0.32)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--nx-card-border)";
        (e.currentTarget as HTMLButtonElement).style.boxShadow = "none";
      }}
    >
      <div className="h-[3px] w-full" style={{ background: "linear-gradient(90deg, #2582EB 0%, #60BDFA 50%, #34d399 100%)" }} />
      <div className="flex-1 flex flex-col px-7 pt-7 pb-7">
        <div className="flex items-start justify-between mb-5">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg" style={{ background: demo.tileGradient }}>
            {demo.icon}
          </div>
          <span
            className="px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-[0.04em]"
            style={{ background: "var(--nx-hover)", border: "1px solid var(--nx-border)", color: "var(--nx-text-3)" }}
          >
            {demo.scale}
          </span>
        </div>

        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] mb-2" style={{ color: "var(--nx-accent-text)" }}>
          {demo.eyebrow}
        </div>
        <div className="text-[22px] font-extrabold tracking-[-0.02em] leading-tight" style={{ color: "var(--nx-text-1)" }}>
          {demo.title}
        </div>
        <div className="text-[12px] font-medium mt-1 mb-4" style={{ color: "var(--nx-text-3)" }}>
          {demo.brand}
        </div>

        <p className="text-[13.5px] leading-relaxed mb-6" style={{ color: "var(--nx-text-2)" }}>
          {demo.desc}
        </p>

        <div className="flex flex-wrap gap-2 mb-7">
          {demo.chips.map((chip) => (
            <span
              key={chip}
              className="px-2.5 py-1 rounded-full text-[11px] font-medium"
              style={{ background: "var(--nx-hover)", border: "1px solid var(--nx-border)", color: "var(--nx-text-2)" }}
            >
              {chip}
            </span>
          ))}
        </div>

        <div className="mt-auto">
          <div
            className="w-full h-11 rounded-xl flex items-center justify-center gap-2 text-[13.5px] font-semibold text-white transition-all duration-150 group-hover:brightness-110"
            style={{ background: "linear-gradient(135deg, #2582EB 0%, #1D6BD8 100%)", boxShadow: "0 4px 20px rgba(37,130,235,0.32)" }}
          >
            Start the demo
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function DemoChooserPage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--nx-bg)", fontFamily: "Nunito, system-ui, sans-serif" }}>
      {/* ── Background atmosphere ── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="orb animate-orb_float" style={{ width: 700, height: 700, top: "-15%", right: "-10%", background: "radial-gradient(circle, rgba(37,130,235,0.13) 0%, transparent 70%)" }} />
        <div className="orb" style={{ width: 500, height: 500, bottom: "5%", left: "-8%", background: "radial-gradient(circle, rgba(37,130,235,0.09) 0%, transparent 70%)", animationDelay: "3s" }} />
        <div className="absolute inset-0 opacity-[0.018]" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      </div>

      {/* ── Nav ── */}
      <nav className="sticky top-0 z-30 flex-shrink-0" style={{ background: "var(--nx-nav)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderBottom: "1px solid var(--nx-border)" }}>
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span style={{ color: "var(--nx-text-1)", display: "inline-flex" }}><NuplayWordmark height={17} /></span>
            <span className="w-px h-5" style={{ background: "var(--nx-border)" }} />
            <span className="text-[9px] font-semibold tracking-[0.16em] uppercase" style={{ color: "var(--nx-text-3)" }}>Experience Studio</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-medium" style={{ background: "rgba(52,211,153,0.10)", border: "1px solid rgba(52,211,153,0.20)", color: "var(--color-success)" }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--color-success)" }} />
            Agents online
          </div>
        </div>
      </nav>

      {/* ── Chooser ── */}
      <div className="relative flex-1">
        <div className="relative max-w-5xl mx-auto px-6 pt-16 pb-20 md:pt-24">
          <div className="text-center max-w-2xl mx-auto mb-12 step-enter">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.09em] uppercase mb-7" style={{ background: "rgba(37,130,235,0.12)", border: "1px solid rgba(37,130,235,0.25)", color: "var(--nx-accent-text)" }}>
              <span className="w-1 h-1 rounded-full" style={{ background: "var(--nx-accent-text)" }} />
              Live voice demos
            </div>
            <h1 className="text-[40px] md:text-[50px] font-extrabold leading-[1.08] tracking-[-0.035em] mb-5" style={{ color: "var(--nx-text-1)" }}>
              Pick a{" "}
              <span style={{ background: "linear-gradient(135deg, #60BDFA 0%, #2582EB 50%, #1D4885 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                demo.
              </span>
            </h1>
            <p className="text-[15.5px] leading-relaxed" style={{ color: "var(--nx-text-2)" }}>
              Five NuPlay voice demos, five very different jobs. Each one runs a real call against a live customer database — no account, no setup.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 items-stretch step-enter" style={{ animationDelay: "60ms" }}>
            {DEMOS.map((demo) => (
              <DemoCard key={demo.route} demo={demo} />
            ))}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer style={{ borderTop: "1px solid var(--nx-border)" }}>
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <span className="text-[13px]" style={{ color: "var(--nx-text-3)" }}>NuPlay Experience Studio — live agent demos</span>
          <span className="flex items-center gap-2 text-[13px]" style={{ color: "var(--nx-text-3)" }}>
            Powered by
            <span style={{ color: "var(--nx-text-1)", display: "inline-flex" }}><NuplayWordmark height={16} /></span>
          </span>
        </div>
      </footer>
    </div>
  );
}
